import axios from 'axios';
import { JWT_HOST_API, TOKEN_STORAGE_KEY } from 'configs/auth.config';
import { supabase } from './supabase.js';

const axiosInstance = axios.create({
  baseURL: JWT_HOST_API,
  timeout: 30000, // Augmenté à 30 secondes pour éviter les timeouts
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur de requête pour ajouter automatiquement le token
axiosInstance.interceptors.request.use(
  async (config) => {
    let token = null;

    // 1. Essayer d'obtenir le token frais via la session Supabase (gère l'auto-refresh)
    if (supabase) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          token = session.access_token;
          // Synchroniser TOKEN_STORAGE_KEY avec le dernier token Supabase
          localStorage.setItem(TOKEN_STORAGE_KEY, token);
        }
      } catch {
        /* supabase session error, fallback below */
      }
    }

    // 2. Fallback: token legacy dans localStorage
    if (!token) {
      token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token === "null" || token === "undefined") token = null;
    }

    // 3. Fallback: clé Supabase brute dans localStorage
    if (!token) {
      try {
        const supabaseKey = Object.keys(localStorage).find(key => key.includes('-auth-token'));
        if (supabaseKey) {
          const supabaseData = JSON.parse(localStorage.getItem(supabaseKey));
          token = supabaseData?.access_token;
        }
      } catch {
        /* token parse error */
      }
    }

    const apiKey = localStorage.getItem('X_API_KEY') || 'TxApp-API-Key-2025';

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse pour gérer les erreurs et la déconnexion automatique
axiosInstance.interceptors.response.use(
  (response) => {
    // Vérifier si la réponse est du HTML au lieu de JSON
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('text/html')) {
      return Promise.reject(new Error('Le serveur a retourné une réponse HTML inattendue. Vérifiez que l\'API est accessible.'));
    }

    return response;
  },
  (error) => {
    // Gestion spécifique des erreurs de réseau/timeout
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return Promise.reject(new Error('La requête a expiré. Vérifiez votre connexion internet.'));
      }
      return Promise.reject(new Error('Erreur de connexion réseau. Vérifiez votre connexion internet.'));
    }

    const { response } = error;

    // Gestion des erreurs d'authentification
    if (response?.status === 401) {
      // Si l'erreur vient de la requête de login, ce n'est pas une expiration de session
      const requestUrl = error.config?.url || '';
      if (requestUrl.includes('/auth/login')) {
        return Promise.reject(new Error('Email ou mot de passe incorrect.'));
      }

      // NE PAS supprimer le token ici — cela cause une cascade de 401 quand
      // plusieurs requêtes concurrentes sont en vol. Le AuthProvider gère
      // la déconnexion via onAuthStateChange de Supabase.
      return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'));
    }

    // Vérifier si la réponse est du HTML (erreur serveur)
    const contentType = response?.headers?.['content-type'];
    if (contentType && contentType.includes('text/html')) {
      return Promise.reject(new Error('Erreur serveur inattendue. L\'API pourrait être indisponible.'));
    }

    // Gestion des messages d'erreur - préserver l'erreur originale autant que possible
    const data = response?.data;
    let message = 'Une erreur réseau est survenue';

    if (typeof data === 'string' && data) {
      message = data;
    } else if (data?.message) {
      message = data.message;
    } else if (data?.error) {
      message = data.error;
    } else if (error?.message && error.message !== 'Request failed with status code ' + response?.status) {
      message = error.message;
    } else if (response?.status) {
      message = `Erreur ${response.status}: ${response.statusText || 'Erreur serveur'}`;
    }

    return Promise.reject(new Error(message));
  }
);

export default axiosInstance;
